import React, { useEffect, useState } from 'react';
import { Circle, Group, Image, Text } from 'react-konva';
import useImage from 'use-image';
import { getInitials } from '@gettactic/helpers';
import { SpotLayout } from '../../types';

const globalImageCache: Record<string, string> = {};

const extractUserId = (url: string): string | null => {
  const userIdPattern =
    /profile-photos\/(user_[a-f0-9\-]{36}\/\/\d{13})\.jpeg/i;
  const match = decodeURIComponent(url).match(userIdPattern);
  return match ? match[1] : null;
};

export type CircleProfilePhotoProps = {
  profilePhoto: string;
  name: string;
  restricted?: boolean;
  privacy?: boolean;
  layout: SpotLayout;
  radius: number;
};

export const CircleProfilePhoto: React.FC<CircleProfilePhotoProps> = ({
  profilePhoto,
  name,
  restricted = false,
  privacy = false,
  layout,
  radius: circleRadius
}) => {
  const [base64Image, setBase64Image] = useState<string | null>(null);
  const [cachedImage] = useImage(base64Image || ''); // Load the base64 image

  useEffect(() => {
    if (!profilePhoto || privacy) return;

    // Check global cache
    if (globalImageCache[profilePhoto]) {
      setBase64Image(globalImageCache[profilePhoto]);
      return;
    }

    const img = new window.Image();

    img.onload = async () => {
      try {
        const response = await fetch(img.src);
        const blob = await response.blob();

        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          globalImageCache[profilePhoto] = dataUrl;
          setBase64Image(dataUrl);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error('CORS prevents converting image to base64', error);
      }
    };

    img.onerror = () => console.error('Failed to load image:', profilePhoto);

    img.crossOrigin = 'Anonymous';
    img.src = profilePhoto.startsWith('https://cdn.gettactic.com/image-crop/')
      ? `http://gondor.gettactic.loc:3000/api/profile-picture?userId=${encodeURIComponent(extractUserId(profilePhoto) || '')}`
      : profilePhoto;
  }, [profilePhoto, privacy]);

  if (privacy) return null;

  const initials = getInitials(name || '');

  // Calculate font size
  const maxFontSize = circleRadius * 0.5;
  const textWidth = circleRadius * 1.5;
  const textHeight = circleRadius * 1.5;

  return (
    <Group
      x={layout.circle.x - circleRadius}
      y={layout.circle.y - circleRadius}
      width={circleRadius * 2}
      height={circleRadius * 2}
      clipFunc={(ctx) => {
        ctx.arc(
          circleRadius,
          circleRadius,
          circleRadius,
          0,
          Math.PI * 2,
          false
        );
        ctx.closePath();
      }}
      opacity={restricted ? 0.5 : 1}
    >
      {cachedImage ? (
        <Image
          image={cachedImage}
          width={circleRadius * 2}
          height={circleRadius * 2}
        />
      ) : (
        <>
          <Circle
            x={circleRadius}
            y={circleRadius}
            radius={circleRadius}
            fill="#223C5B"
            strokeWidth={0}
          />
          <Text
            text={initials}
            fontSize={maxFontSize}
            fontStyle="bold"
            fill="white"
            align="center"
            verticalAlign="middle"
            width={textWidth}
            height={textHeight}
            x={(circleRadius * 2 - textWidth) / 2}
            y={(circleRadius * 2 - textHeight) / 2}
          />
        </>
      )}
    </Group>
  );
};
